import React, { useState, useRef, useLayoutEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { TableSkeleton } from "../components/Skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { fmtFCFA, fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Download, ArrowLeft, Pencil, Heading, GripVertical, ChevronUp, ChevronDown, Copy, Mail, ClipboardPaste, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { MaterialPicker, ManageMaterials, UnitSelect } from "../components/EstimateMaterials";
import { SectionPicker, ManageSectionTitles } from "../components/EstimateSections";
import { ImportItemsDialog, readClipboard, writeClipboard, cleanItems } from "../components/EstimateClipboard";
import PhoneField from "../components/PhoneField";
import { isValidPhoneNumber } from "react-phone-number-input";
import { Checkbox } from "../components/ui/checkbox";

const today = () => new Date().toISOString().slice(0, 10);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const toE164 = (p) => {
  const d = (p || "").replace(/[\s\-().]/g, "");
  if (!d) return "";
  if (d.startsWith("+")) return d;
  if (d.startsWith("00")) return `+${d.slice(2)}`;
  if (d.startsWith("237") && d.length === 12) return `+${d}`;
  return `+237${d.replace(/^0/, "")}`;
};
const blankItem = () => ({ kind: "item", item: "", description: "", unit: "", quantity: "", unit_cost: "" });
const isSec = (it) => it.kind === "section";
const isBlank = (it) => !isSec(it) && !it.item.trim() && !it.description.trim() && !num(it.quantity) && !num(it.unit_cost);
const blankForm = () => ({ title: "", client: "", client_address: "", client_email: "", client_phone: "", status: "Draft", estimate_date: today(), valid_until: "", vat_percent: 0, discount_amount: 0, labour_percent: 30, notes: "", notes_style: "lower", items: [blankItem()] });
const STATUS_STYLES = { Draft: "bg-slate-100 text-slate-600", Sent: "bg-blue-50 text-blue-700", Approved: "bg-emerald-50 text-emerald-700", Rejected: "bg-red-50 text-red-700" };
const num = (v) => (parseFloat(v) || 0);
const letter = (i) => (i < 26 ? String.fromCharCode(97 + i) : String.fromCharCode(96 + Math.floor(i / 26)) + String.fromCharCode(97 + (i % 26)));
const roman = (n) => [[10, "x"], [9, "ix"], [5, "v"], [4, "iv"], [1, "i"]].reduce((s, [v, r]) => { while (n >= v) { s += r; n -= v; } return s; }, "");
export const NOTE_STYLES = [
  { id: "lower", label: "a) b) c)" }, { id: "upper", label: "A) B) C)" }, { id: "number", label: "1. 2. 3." },
  { id: "roman", label: "i. ii. iii." }, { id: "bullet", label: "• Bullets" },
];
const marker = (style, i) => ({ upper: `${letter(i).toUpperCase()})`, number: `${i + 1}.`, roman: `${roman(i + 1)}.`, bullet: "•" }[style] || `${letter(i)})`);
const NOTE_PREFIX = /^\s*(?:(?:[a-zA-Z]{1,2}\)|[a-zA-Z]\.|[ivxlcIVXLC]{1,6}[.)]|\d+[.)])\s+|[-•*]\s+)/;
const reletter = (text, style) => text.split("\n").map((l) => l.replace(NOTE_PREFIX, "").trim()).filter(Boolean).map((l, i) => `${marker(style, i)} ${l}`).join("\n");

const RowHandle = ({ idx, count, onMove, onDragStart, onDragEnd, label, checked, onCheck }) => (
  <div className="flex items-center gap-1">
    <Checkbox checked={checked} onCheckedChange={onCheck} data-testid={`estimate-row-select-${idx}`} className="mr-1" />
    <button type="button" draggable onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(idx)); onDragStart(idx); }} onDragEnd={onDragEnd}
      title="Drag to reorder" data-testid={`estimate-row-drag-${idx}`} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-600"><GripVertical className="h-4 w-4" /></button>
    <div className="flex flex-col -my-1">
      <button type="button" disabled={idx === 0} onClick={() => onMove(idx, idx - 1)} title="Move up" data-testid={`estimate-row-up-${idx}`} className="text-slate-300 hover:text-[#1E3A8A] disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
      <button type="button" disabled={idx === count - 1} onClick={() => onMove(idx, idx + 1)} title="Move down" data-testid={`estimate-row-down-${idx}`} className="text-slate-300 hover:text-[#1E3A8A] disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
    </div>
    <span className="ml-0.5">{label}</span>
  </div>
);

const RowActions = ({ idx, kind, onInsert, onRemove }) => (
  <div className="flex items-center justify-center gap-2">
    <button type="button" onClick={() => onInsert(idx)} title="Insert a line below" data-testid={`estimate-${kind}-insert-below-${idx}`} className="text-slate-400 hover:text-[#1E3A8A]"><Plus className="h-4 w-4" /></button>
    <button type="button" onClick={() => onRemove(idx)} data-testid={`estimate-${kind}-remove-${idx}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
  </div>
);

const LetteredNotes = ({ value, onChange, style }) => {
  const ref = useRef(null);
  const caret = useRef(null);
  useLayoutEffect(() => {
    if (caret.current !== null && ref.current) {
      ref.current.selectionStart = ref.current.selectionEnd = caret.current;
      caret.current = null;
    }
  }, [value]);
  const onKeyDown = (e) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    e.preventDefault();
    const el = e.target;
    const before = value.slice(0, el.selectionStart);
    const ins = `\n${marker(style, before.split("\n").length)} `;
    caret.current = before.length + ins.length;
    onChange(before + ins + value.slice(el.selectionEnd));
  };
  return (
    <Textarea ref={ref} data-testid="estimate-notes" rows={5} value={value} onKeyDown={onKeyDown}
      onFocus={() => { if (!value.trim()) onChange(`${marker(style, 0)} `); }}
      onBlur={() => onChange(reletter(value, style))}
      onChange={(e) => onChange(e.target.value)} placeholder={"a) Payment terms…\nb) Validity…"} />
  );
};

export default function Estimates({ type = "standard" }) {
  const isC = type === "contract";
  const noun = isC ? "Quotation" : "Estimate";
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to)) { toast.error("Enter a valid email address."); return; }
    setSendingEmail(true);
    try {
      await api.post(`/estimates/${emailTarget.id}/email`, { to });
      qc.invalidateQueries({ queryKey: ["estimates"] });
      toast.success(`Quotation emailed to ${to}.`); setEmailTarget(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send email."); }
    finally { setSendingEmail(false); }
  };
  const qc = useQueryClient();
  const [mode, setMode] = useState("list");
  const [form, setForm] = useState(blankForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: rows = [], isLoading } = useQuery({ queryKey: ["estimates", type], queryFn: () => api.get("/estimates", { params: { type } }).then((r) => r.data) });
  const { data: defaults } = useQuery({ queryKey: ["estimate-defaults"], queryFn: () => api.get("/estimate-defaults").then((r) => r.data) });
  const shown = statusFilter === "all" ? rows : rows.filter((e) => (e.status || "Draft") === statusFilter);

  const startNew = () => {
    const valid = isC ? new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10) : "";
    setForm({ ...blankForm(), valid_until: valid, labour_percent: defaults?.labour_percent ?? 30 }); setEditingId(null); setInsertAt(null); setSelected(new Set()); setClip(readClipboard()); setMode("form");
  };
  const startEdit = (e) => {
    setForm({
      title: e.title || "", client: e.client || "", client_address: e.client_address || "", client_email: e.client_email || "", client_phone: toE164(e.client_phone),
      status: e.status || "Draft", estimate_date: (e.estimate_date || "").slice(0, 10) || today(), valid_until: (e.valid_until || "").slice(0, 10),
      vat_percent: e.vat_percent || 0, discount_amount: e.discount_amount || 0, labour_percent: e.labour_percent ?? 0, notes: e.notes || "", notes_style: e.notes_style || "lower",
      items: (e.items || []).map((it) => ({ kind: it.kind || "item", item: it.item || "", description: it.description || "", unit: it.unit || "", quantity: it.quantity ?? "", unit_cost: it.unit_cost ?? "" })),
    });
    if (!(e.items || []).length) setForm((f) => ({ ...f, items: [blankItem()] }));
    setEditingId(e.id); setInsertAt(null); setSelected(new Set()); setClip(readClipboard()); setMode("form");
  };

  const [insertAt, setInsertAt] = useState(null);
  const setItem = (idx, key, val) => { if (idx === insertAt) setInsertAt(idx + 1); setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) })); };
  const addItem = () => { setInsertAt(null); setForm((f) => ({ ...f, items: [...f.items, blankItem()] })); };
  const placeLines = (lines) => {
    const items = [...form.items];
    if (insertAt === null || insertAt > items.length) {
      if (items.length && isBlank(items[items.length - 1])) items.pop();
      items.push(...lines);
    } else {
      items.splice(insertAt, insertAt < items.length && isBlank(items[insertAt]) ? 1 : 0, ...lines);
      setInsertAt(insertAt + lines.length);
    }
    setSelected(new Set()); setForm((f) => ({ ...f, items }));
  };
  const insertBelow = (idx) => {
    setSelected(new Set());
    setForm((f) => { const items = [...f.items]; items.splice(idx + 1, 0, blankItem()); return { ...f, items }; });
    setInsertAt(idx + 1);
    setTimeout(() => document.querySelector(`[data-testid="estimate-item-name-${idx + 1}"]`)?.focus(), 50);
  };
  const addMaterial = (m) => {
    const target = insertAt ?? form.items.length;
    const lastSec = form.items.slice(0, target).map(isSec).lastIndexOf(true);
    const nextSec = form.items.findIndex((it, i) => i >= target && isSec(it));
    const end = nextSec < 0 ? form.items.length : nextSec;
    const dup = form.items.findIndex((it, i) => i > lastSec && i < end && !isSec(it) && it.item.trim().toLowerCase() === m.name.trim().toLowerCase());
    if (dup >= 0) {
      setItem(dup, "quantity", String(num(form.items[dup].quantity) + 1));
      toast.success(`${m.name}: quantity now ${num(form.items[dup].quantity) + 1}`);
      return;
    }
    addNewMaterialLine(m);
  };
  const addNewMaterialLine = (m) => placeLines([{ ...blankItem(), item: m.name, unit: m.unit || "", quantity: "1", unit_cost: m.unit_cost ? String(m.unit_cost) : "" }]);
  const addSection = (name) => {
    const sec = { ...blankItem(), kind: "section", item: name };
    placeLines(insertAt === null ? [sec, blankItem()] : [sec]);
  };
  const [selected, setSelected] = useState(new Set());
  const [clip, setClip] = useState(readClipboard);
  const toggleSel = (idx) => setSelected((s) => { const n = new Set(s); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  const appendItems = (lines) => placeLines(cleanItems(lines));
  const copySelected = () => {
    const lines = form.items.filter((it, i) => selected.has(i) && (isSec(it) ? it.item.trim() : !isBlank(it)));
    if (!lines.length) { toast.error("Tick the lines you want to copy."); return; }
    writeClipboard(lines); setClip(readClipboard()); setSelected(new Set());
    toast.success(`${lines.length} line${lines.length === 1 ? "" : "s"} copied. Open any estimate or quotation and click Paste.`);
  };
  const pasteClip = () => {
    const lines = readClipboard();
    if (!lines.length) { toast.error("Nothing copied yet."); return; }
    appendItems(lines); toast.success(`${lines.length} line${lines.length === 1 ? "" : "s"} pasted.`);
  };
  const deleteSelected = () => { setInsertAt(null); setForm((f) => { const items = f.items.filter((_, i) => !selected.has(i)); return { ...f, items: items.length ? items : [blankItem()] }; }); setSelected(new Set()); };
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const moveItem = (from, to) => { setSelected(new Set()); setInsertAt(null); setForm((f) => {
    if (from === to || to < 0 || to >= f.items.length) return f;
    const items = [...f.items];
    const [row] = items.splice(from, 1);
    items.splice(to, 0, row);
    return { ...f, items };
  }); };
  const endDrag = () => { setDragIdx(null); setOverIdx(null); };
  const rowDnd = (idx) => ({
    onDragOver: (e) => { if (dragIdx === null) return; e.preventDefault(); if (overIdx !== idx) setOverIdx(idx); },
    onDrop: (e) => { e.preventDefault(); if (dragIdx !== null) moveItem(dragIdx, idx); endDrag(); },
  });
  const dndCls = (idx) => (dragIdx === idx ? " opacity-40" : overIdx === idx && dragIdx !== null ? (dragIdx < idx ? " shadow-[inset_0_-2px_0_#1E3A8A]" : " shadow-[inset_0_2px_0_#1E3A8A]") : "");
  const insCls = (idx) => (insertAt === idx ? " shadow-[inset_0_2px_0_#F97316]" : insertAt === form.items.length && idx === form.items.length - 1 ? " shadow-[inset_0_-2px_0_#F97316]" : "");
  const handle = (idx, label) => <RowHandle idx={idx} count={form.items.length} onMove={moveItem} onDragStart={setDragIdx} onDragEnd={endDrag} label={label} checked={selected.has(idx)} onCheck={() => toggleSel(idx)} />;
  const removeItem = (idx) => { setSelected(new Set()); setInsertAt(null); setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items })); };

  const pct = num(form.labour_percent);
  const price = (it) => num(it.unit_cost) * (isC ? 1 + pct / 100 : 1);
  const materials = form.items.reduce((s, it) => s + (isSec(it) ? 0 : num(it.quantity) * num(it.unit_cost)), 0);
  const subtotal = form.items.reduce((s, it) => s + (isSec(it) ? 0 : num(it.quantity) * price(it)), 0);
  const discount = Math.min(num(form.discount_amount), subtotal);
  const vatAmount = (subtotal - discount) * num(form.vat_percent) / 100;
  const labour = isC ? subtotal - materials : subtotal * pct / 100;
  const grand = subtotal - discount + vatAmount + (isC ? 0 : labour);

  const emailBad = !!form.client_email.trim() && !EMAIL_RE.test(form.client_email.trim());
  const phoneBad = !!form.client_phone && !isValidPhoneNumber(form.client_phone);
  const buildPayload = () => ({
    ...form, estimate_type: type, vat_percent: num(form.vat_percent), discount_amount: num(form.discount_amount), labour_percent: num(form.labour_percent),
    items: form.items.filter((it) => it.item.trim() || (!isSec(it) && (num(it.quantity) || num(it.unit_cost))))
      .map((it) => ({ ...it, quantity: num(it.quantity), unit_cost: num(it.unit_cost) })),
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const openPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/estimates/preview.pdf", buildPayload(), { params: editingId ? { estimate_id: editingId } : {}, responseType: "blob" });
      setPreview(URL.createObjectURL(new Blob([data], { type: "application/pdf" })));
    } catch { toast.error("Could not build the preview."); }
    finally { setPreviewing(false); }
  };
  const closePreview = () => { if (preview) URL.revokeObjectURL(preview); setPreview(null); };
  const save = async () => {
    if (!form.client.trim()) { toast.error("Client is required."); return; }
    if (emailBad) { toast.error("Enter a valid client email or leave it empty."); return; }
    if (phoneBad) { toast.error("Enter a complete client phone number or leave it empty."); return; }
    if (!form.items.some((it) => !isSec(it) && it.item.trim())) { toast.error("Add at least one item/activity."); return; }
    setSaving(true);
    const payload = buildPayload();
    try {
      if (editingId) await api.put(`/estimates/${editingId}`, payload);
      else await api.post("/estimates", payload);
      qc.invalidateQueries({ queryKey: ["estimates"] });
      toast.success(editingId ? `${noun} updated.` : `${noun} created.`);
      setMode("list");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save estimate."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/estimates/${id}`); qc.invalidateQueries({ queryKey: ["estimates"] }); toast.success("Estimate deleted."); }
    catch { toast.error("Could not delete."); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/estimates/${id}/status`, { status }); qc.invalidateQueries({ queryKey: ["estimates"] }); }
    catch { toast.error("Could not update status."); }
  };
  const downloadPdf = (e) => downloadExport(`/export/estimate/${e.id}.pdf`, `${isC ? "quotation" : "estimate"}_${e.number}.pdf`);
  const copyTo = async (e) => {
    try {
      const { data } = await api.post(`/estimates/${e.id}/copy`, { to: isC ? "standard" : "contract" });
      qc.invalidateQueries({ queryKey: ["estimates"] });
      toast.success(`Copied to ${isC ? "Estimates" : "Quotation"} as ${data.number}.`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not copy."); }
  };

  if (mode === "form") {
    return (
      <div className="space-y-6" data-testid="estimate-form">
        <div className="flex items-center justify-between">
          <PageHeader title={editingId ? `Edit ${noun}` : `New ${noun}`} subtitle={isC ? "The labour % is added into every item's price — the client never sees a separate labour line." : "Fill the details, add items, then save. The PDF carries the official company letterhead."} />
          <Button variant="outline" onClick={() => setMode("list")}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Title / Subject</Label><Input data-testid="estimate-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={isC ? "e.g. Electrical installation quotation" : "e.g. Solar installation estimate"} /></div>
            <div className="space-y-1.5"><Label>Client *</Label><Input data-testid="estimate-client" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} placeholder="Client name" /></div>
            <div className="space-y-1.5"><Label>Client address</Label><Input data-testid="estimate-client-address" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} placeholder="Street, City" /></div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Client email</Label><Input type="email" inputMode="email" autoComplete="email" data-testid="estimate-client-email" value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value.replace(/\s/g, "") })} onBlur={() => setForm((f) => ({ ...f, client_email: f.client_email.trim().toLowerCase() }))}
              placeholder="client@email.com" className={emailBad ? "border-red-400 focus-visible:ring-red-400" : ""} />
              {emailBad && <p className="text-xs text-red-600" data-testid="estimate-client-email-error">Enter a valid email, e.g. name@company.com</p>}</div>
            <div className="space-y-1.5"><Label>Client phone</Label><PhoneField testId="estimate-client-phone" invalid={phoneBad} value={form.client_phone} onChange={(v) => setForm((f) => ({ ...f, client_phone: v }))} />
              {phoneBad && <p className="text-xs text-red-600" data-testid="estimate-client-phone-error">This number doesn't look complete for the selected country.</p>}</div>
            <div className="space-y-1.5"><Label>{isC ? "Quotation date" : "Estimate date"}</Label><Input type="date" data-testid="estimate-date" value={form.estimate_date} onChange={(e) => setForm({ ...form, estimate_date: e.target.value })} /></div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Valid until</Label><Input type="date" data-testid="estimate-valid-until" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Discount (FCFA)</Label><Input type="number" min="0" step="any" data-testid="estimate-discount" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>VAT %</Label><Input type="number" min="0" step="0.01" data-testid="estimate-vat" value={form.vat_percent} onChange={(e) => setForm({ ...form, vat_percent: e.target.value })} /></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-700">Materials / Items</span>
              {insertAt !== null && (
                <span data-testid="estimate-insert-point" className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-xs px-2.5 py-0.5">
                  New lines go at position {insertAt + 1}
                  <button type="button" onClick={() => setInsertAt(null)} data-testid="estimate-insert-point-clear" className="font-semibold hover:underline">Done</button>
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2"><ManageMaterials /><MaterialPicker onPick={addMaterial} /></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left w-28">
                    <span className="inline-flex items-center gap-1.5">S/N
                      <button type="button" onClick={() => insertBelow(-1)} title="Insert a line at the top" data-testid="estimate-insert-top" className="text-slate-400 hover:text-[#1E3A8A]"><Plus className="h-3.5 w-3.5" /></button>
                    </span>
                  </th>
                  <th className="px-3 py-2 text-left">Item/Activity</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left w-28">Unit</th>
                  <th className="px-3 py-2 text-right w-24">Quantity</th>
                  <th className="px-3 py-2 text-right w-32">{isC ? "Base unit cost" : "Unit cost"}</th>
                  <th className="px-3 py-2 text-right w-36">Total</th>
                  <th className="px-3 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => isSec(it) ? (
                  <tr key={idx} className={`border-t border-slate-200 bg-blue-50/60${dndCls(idx)}${insCls(idx)}`} data-testid={`estimate-section-${idx}`} {...rowDnd(idx)}>
                    <td className="px-3 py-2 text-[#1E3A8A]">{handle(idx, <Heading className="h-4 w-4" />)}</td>
                    <td className="px-2 py-1.5" colSpan={6}>
                      <Input data-testid={`estimate-section-name-${idx}`} value={it.item} onChange={(e) => setItem(idx, "item", e.target.value)} placeholder="Section title" className="font-semibold text-[#1E3A8A] uppercase tracking-wide bg-white" />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <RowActions idx={idx} kind="section" onInsert={insertBelow} onRemove={removeItem} />
                    </td>
                  </tr>
                ) : (
                  <tr key={idx} className={`border-t border-slate-100${dndCls(idx)}${insCls(idx)}`} data-testid={`estimate-item-${idx}`} {...rowDnd(idx)}>
                    <td className="px-3 py-2 text-slate-500">{handle(idx, form.items.slice(0, idx + 1).filter((x) => !isSec(x)).length)}</td>
                    <td className="px-2 py-1.5"><Input data-testid={`estimate-item-name-${idx}`} value={it.item} onChange={(e) => setItem(idx, "item", e.target.value)} placeholder="Item / activity" /></td>
                    <td className="px-2 py-1.5"><Input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Details" /></td>
                    <td className="px-2 py-1.5"><UnitSelect value={it.unit} onChange={(v) => setItem(idx, "unit", v)} testid={`estimate-item-unit-${idx}`} /></td>
                    <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" data-testid={`estimate-item-qty-${idx}`} value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} /></td>
                    <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" data-testid={`estimate-item-cost-${idx}`} value={it.unit_cost} onChange={(e) => setItem(idx, "unit_cost", e.target.value)} />
                      {isC && num(it.unit_cost) > 0 && <div className="text-[11px] text-right text-[#1E3A8A] mt-0.5 font-mono-num whitespace-nowrap" data-testid={`estimate-item-price-${idx}`}>{fmtFCFA(price(it))} incl. labour</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num whitespace-nowrap" data-testid={`estimate-item-total-${idx}`}>{fmtFCFA(num(it.quantity) * price(it))}</td>
                    <td className="px-2 py-2 text-center">
                      <RowActions idx={idx} kind="item" onInsert={insertBelow} onRemove={removeItem} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={addItem} data-testid="estimate-add-item"><Plus className="h-4 w-4 mr-2" /> Add item</Button>
            <SectionPicker onPick={addSection} />
            <ManageSectionTitles />
            <span className="hidden sm:block h-6 w-px bg-slate-200 mx-1" />
            <Button variant="outline" onClick={copySelected} disabled={!selected.size} data-testid="estimate-copy-lines"><Copy className="h-4 w-4 mr-2" /> Copy{selected.size ? ` (${selected.size})` : ""}</Button>
            <Button variant="outline" onClick={pasteClip} disabled={!clip.length} data-testid="estimate-paste-lines" title={clip.length ? `Paste ${clip.length} copied line(s)` : "Copy lines first"}><ClipboardPaste className="h-4 w-4 mr-2" /> Paste{clip.length ? ` (${clip.length})` : ""}</Button>
            {selected.size > 0 && <Button variant="ghost" onClick={deleteSelected} data-testid="estimate-delete-lines" className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Delete ({selected.size})</Button>}
            <ImportItemsDialog excludeId={editingId} onImport={(lines) => { appendItems(lines); toast.success(`${lines.length} line${lines.length === 1 ? "" : "s"} imported.`); }} />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label>Notes / Terms <span className="text-xs font-normal text-slate-400">— one note per line</span></Label>
              <select data-testid="estimate-notes-style" value={form.notes_style}
                onChange={(e) => setForm((f) => ({ ...f, notes_style: e.target.value, notes: f.notes.trim() ? reletter(f.notes, e.target.value) : "" }))}
                className="h-8 rounded-md border border-input bg-transparent px-2 text-xs">
                {NOTE_STYLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <LetteredNotes value={form.notes} style={form.notes_style} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-2 text-sm">
            {isC && (
              <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100">
                <span className="text-slate-500 flex items-center gap-1.5">Labour
                  <Input type="number" min="0" step="any" data-testid="estimate-labour-percent" value={form.labour_percent}
                    onChange={(e) => setForm({ ...form, labour_percent: e.target.value })} className="h-7 w-16 px-1.5 text-right text-xs" />%
                  <span className="text-xs text-slate-400">added to each item</span>
                </span>
                <span className="text-xs text-slate-400" data-testid="estimate-labour-amount">internal: {fmtFCFA(labour)}</span>
              </div>
            )}
            <div className="flex justify-between"><span className="text-slate-500">Subtotal{isC ? " (incl. labour)" : ""}</span><span className="font-mono-num" data-testid="estimate-subtotal">{fmtFCFA(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Discount</span><span className="font-mono-num">- {fmtFCFA(discount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">VAT ({num(form.vat_percent)}%)</span><span className="font-mono-num">{fmtFCFA(vatAmount)}</span></div>
            {!isC && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-500 flex items-center gap-1.5">Labour
                <Input type="number" min="0" step="any" data-testid="estimate-labour-percent" value={form.labour_percent}
                  onChange={(e) => setForm({ ...form, labour_percent: e.target.value })} className="h-7 w-16 px-1.5 text-right text-xs" />%
                <span className="text-xs text-slate-400">of materials</span>
              </span>
              <span className="font-mono-num" data-testid="estimate-labour-amount">{fmtFCFA(labour)}</span>
            </div>
            )}
            <div className="flex justify-between text-base font-semibold border-t border-slate-200 pt-2"><span>Grand Total</span><span className="font-mono-num text-[#1E3A8A]" data-testid="estimate-grand-total">{fmtFCFA(grand)}</span></div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => setMode("list")}>Cancel</Button>
          <Button variant="outline" onClick={openPreview} disabled={previewing} data-testid="estimate-preview-pdf"><Eye className="h-4 w-4 mr-2" />{previewing ? "Building…" : "Preview PDF"}</Button>
          <Button onClick={save} disabled={saving} data-testid="estimate-save" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{saving ? "Saving…" : (editingId ? `Update ${noun}` : `Save ${noun}`)}</Button>
        </div>
        <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
          <DialogContent className="max-w-5xl w-[95vw] max-h-[94vh] p-3 sm:p-4" data-testid="estimate-preview-dialog">
            <DialogHeader>
              <DialogTitle>{noun} preview</DialogTitle>
              <DialogDescription>This is how the PDF will look with the current (unsaved) changes.</DialogDescription>
            </DialogHeader>
            {preview && <iframe title="PDF preview" src={preview} className="w-full h-[calc(88vh-170px)] min-h-[300px] rounded-md border border-slate-200 bg-slate-50" data-testid="estimate-preview-frame" />}
            <DialogFooter className="gap-2">
              <span className="text-xs text-slate-400 self-center sm:mr-auto">Preview not showing on your phone? Use Open in new tab.</span>
              <a href={preview || "#"} target="_blank" rel="noreferrer" className="text-sm text-[#1E3A8A] underline self-center" data-testid="estimate-preview-newtab">Open in new tab</a>
              <a href={preview || "#"} download={`${isC ? "quotation" : "estimate"}_preview.pdf`} className="text-sm text-[#1E3A8A] underline self-center" data-testid="estimate-preview-download">Download</a>
              <Button onClick={closePreview} variant="outline">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="estimates-page">
      <div className="flex items-center justify-between">
        <PageHeader title={isC ? "Quotation" : "Estimates"} subtitle={isC ? "Client quotations with labour built into each item's price. Email them straight to the client." : "Build itemised cost estimates on the official company letterhead."} />
        <Button onClick={startNew} data-testid="new-estimate-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> New {noun}</Button>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {["all", "Draft", "Sent", "Approved", "Rejected"].map((s) => (
            <button key={s} data-testid={`estimate-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${statusFilter === s ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? <TableSkeleton cols={6} /> : rows.length === 0 ? (
          <EmptyState text={isC ? "No quotations yet." : "No estimates yet."} action={<Button onClick={startNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> New {noun}</Button>} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Number</TableHead><TableHead>Title</TableHead><TableHead>Client</TableHead>
                <TableHead>Date</TableHead><TableHead className="text-right">Grand Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((e) => (
                <TableRow key={e.id} data-testid="estimate-row">
                  <TableCell className="font-semibold">{e.number}</TableCell>
                  <TableCell>{e.title || "—"}</TableCell>
                  <TableCell>{e.client}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(e.estimate_date)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(e.grand_total)}</TableCell>
                  <TableCell>
                    <select value={e.status || "Draft"} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`estimate-status-${e.id}`}
                      className={`text-xs font-semibold rounded-full px-2 py-1 border-0 cursor-pointer ${STATUS_STYLES[e.status] || STATUS_STYLES.Draft}`}>
                      {["Draft", "Sent", "Approved", "Rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(e)} data-testid={`estimate-pdf-${e.id}`} title="Download PDF"><Download className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => startEdit(e)} data-testid={`estimate-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" onClick={() => copyTo(e)} data-testid={`estimate-copy-${e.id}`} title={isC ? "Copy to Estimates (separate labour line)" : "Copy to Quotation (labour in item prices)"}><Copy className="h-4 w-4" /></Button>
                      {isC && <Button size="sm" variant="outline" onClick={() => { setEmailTo(e.client_email || ""); setEmailTarget(e); }} data-testid={`estimate-email-${e.id}`} title={e.emailed_to ? `Last emailed to ${e.emailed_to}` : "Email to client"}><Mail className="h-4 w-4" /></Button>}
                      <Confirm onConfirm={() => remove(e.id)} title="Delete estimate?" description={`This permanently removes ${e.number}.`}
                        trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid={`estimate-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <Dialog open={!!emailTarget} onOpenChange={(o) => { if (!o) setEmailTarget(null); }}>
        <DialogContent data-testid="estimate-email-dialog">
          <DialogHeader>
            <DialogTitle>Email quotation</DialogTitle>
            <DialogDescription>Sends <span className="font-semibold">{emailTarget?.number}</span> to the client with a secure link to download the PDF. Draft quotations are marked Sent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5"><Label>Client email</Label><Input type="email" data-testid="estimate-email-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="client@email.com" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} data-testid="estimate-email-send" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{sendingEmail ? "Sending…" : "Send"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
